import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/Admin/AdminLayout';
import ImageUpload from '../../components/Admin/ImageUpload';
import blogService from '../../services/blogService';
import uploadService from '../../services/uploadService';
import { useToast } from '../../context/ToastContext';

export default function BlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    category: 'Marketing',
    author: '',
    image: '',
    tags: [],
    content: {
      intro: '',
      sections: []
    },
    is_published: true,
  });
  const [tagInput, setTagInput] = useState('');

  // File states for new uploads
  const [blogImageFile, setBlogImageFile] = useState(null);


  const fetchBlog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogService.getBlogById(id);

      if (response.success && response.data) {
        // Backend returns blog directly in data, not data.blog
        const blog = response.data;

        // Backend uses content_intro and content_sections, convert to content object
        const content = {
          intro: blog.content_intro || '',
          sections: (blog.content_sections || []).map(section => ({
            heading: section.heading || '',
            content: section.content || '',
            images: section.images || []
          }))
        };

        setFormData({
          title: blog.title || '',
          slug: blog.slug || '',
          description: blog.description || '',
          category: blog.category || 'Marketing',
          author: blog.author || '',
          image: blog.image || '',
          tags: Array.isArray(blog.tags) ? blog.tags : [],
          content: content,
          // Backend uses 'published' (0 or 1), convert to boolean
          is_published: blog.published === 1 || blog.is_published === true,
        });
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch blog data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchBlog();
    }
  }, [id, isEditMode, fetchBlog]);

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'title') {
      setFormData({
        ...formData,
        title: value,
        slug: generateSlug(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const tag = tagInput.trim().replace(/,$/, '');
      if (tag && !formData.tags.includes(tag)) {
        setFormData({
          ...formData,
          tags: [...formData.tags, tag]
        });
      }
      setTagInput('');
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      const newTags = [...formData.tags];
      newTags.pop();
      setFormData({ ...formData, tags: newTags });
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleContentChange = (field, value) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        [field]: value,
      },
    });
  };

  const handleSectionChange = (index, field, value) => {
    const sections = [...formData.content.sections];
    sections[index] = {
      ...sections[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections,
      },
    });
  };

  const handleSectionImageSelect = async (sectionIndex, imageIndex, file) => {
    if (!file) return;

    try {
      setUploadProgress({ [`section-${sectionIndex}-${imageIndex}`]: 0 });
      const uploadResponse = await uploadService.uploadWithProgress(
        file,
        'blog',
        (progress) => setUploadProgress({ [`section-${sectionIndex}-${imageIndex}`]: progress })
      );

      if (uploadResponse.success && uploadResponse.data) {
        const sections = [...formData.content.sections];
        if (!sections[sectionIndex].images) {
          sections[sectionIndex].images = [];
        }
        sections[sectionIndex].images[imageIndex] = uploadResponse.data.url;
        setFormData({
          ...formData,
          content: {
            ...formData.content,
            sections,
          },
        });
      }
    } catch (err) {
      console.error('Error uploading section image:', err);
      addToast('Failed to upload image. Please try again.', 'error');
    } finally {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[`section-${sectionIndex}-${imageIndex}`];
        return newProgress;
      });
    }
  };

  const handleRemoveSectionImage = (sectionIndex, imageIndex) => {
    const sections = [...formData.content.sections];
    if (sections[sectionIndex].images) {
      sections[sectionIndex].images.splice(imageIndex, 1);
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          sections,
        },
      });
    }
  };

  const handleAddSectionImage = (sectionIndex) => {
    const sections = [...formData.content.sections];
    if (!sections[sectionIndex].images) {
      sections[sectionIndex].images = [];
    }
    sections[sectionIndex].images.push('');
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections,
      },
    });
  };

  const addSection = () => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections: [
          ...formData.content.sections,
          { heading: '', content: '', images: [] },
        ],
      },
    });
  };

  const removeSection = (index) => {
    const sections = formData.content.sections.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Upload images if new files are selected
      let blogImageUrl = formData.image;

      if (blogImageFile) {
        setUploadProgress({ blog: 0 });
        const uploadResponse = await uploadService.uploadWithProgress(
          blogImageFile,
          'blog',
          (progress) => setUploadProgress({ blog: progress })
        );
        if (uploadResponse.success && uploadResponse.data) {
          blogImageUrl = uploadResponse.data.url;
        }
      }

      // Prepare blog data - backend expects content_intro and content_sections separately
      const blogData = {
        title: formData.title,
        slug: formData.slug,
        description: formData.description,
        category: formData.category,
        author: formData.author,
        image: blogImageUrl,
        content_intro: formData.content.intro || '',
        content_sections: formData.content.sections || [],
        tags: formData.tags || [],
        published: formData.is_published ? 1 : 0, // Backend expects 0 or 1
      };

      // Create or update blog
      let response;
      if (isEditMode) {
        response = await blogService.updateBlog(id, blogData);
      } else {
        response = await blogService.createBlog(blogData);
      }

      if (response.success) {
        addToast(isEditMode ? 'Blog updated successfully!' : 'Blog created successfully!', 'success');
        navigate('/admin/blogs');
      } else {
        setError(response.message || 'Failed to save blog. Please try again.');
      }
    } catch (err) {
      console.error('Error saving blog:', err);
      setError(err.response?.data?.message || 'Failed to save blog. Please try again.');
    } finally {
      setSaving(false);
      setUploadProgress({});
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12 h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading blog...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="flex flex-col bg-[#0f0716] rounded-xl shadow-2xl border border-white/5">
        {/* Sticky Header with Actions */}
        <div className="shrink-0 bg-[#1a1025] border-b border-white/5 p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-white">
              {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {isEditMode ? 'Update your blog content details below' : 'Fill in the details to create a new blog entry'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/blogs')}
              className="px-4 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-lg shadow-blue-900/20"
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Post' : 'Publish Post'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 lg:p-8">
          {error && (
            <div className="max-w-[1600px] mx-auto bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8">

            {/* LEFT COLUMN - Main Content (8 cols) */}
            <div className="col-span-12 lg:col-span-8 space-y-8">

              {/* Title & Description Card */}
              <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-white mb-6 border-b border-white/5 pb-4">General Information</h3>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0a0118] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-white placeholder-gray-600 text-lg transition-all"
                      placeholder="Enter a descriptive blog title"
                    />
                  </div>

                  <div>
                    <label htmlFor="slug" className="block text-sm font-medium text-gray-300 mb-2">
                      Slug *
                    </label>
                    <div className="flex bg-[#0a0118] border border-white/10 rounded-lg focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500 transition-all">
                      <span className="pl-4 py-3 text-gray-500 select-none">/blog/</span>
                      <input
                        type="text"
                        id="slug"
                        name="slug"
                        required
                        value={formData.slug}
                        onChange={handleInputChange}
                        className="flex-1 px-2 py-3 bg-transparent border-none focus:outline-none text-white placeholder-gray-600"
                        placeholder="post-url-slug"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                      Short Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0a0118] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-white placeholder-gray-600 transition-all"
                      placeholder="Write a brief excerpt for SEO and listings..."
                    />
                  </div>
                </div>
              </div>

              {/* Content Editor Card */}
              <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-white mb-6 border-b border-white/5 pb-4">Content Editor</h3>

                <div className="space-y-8">
                  <div>
                    <label htmlFor="intro" className="block text-sm font-medium text-gray-300 mb-2">
                      Introduction
                    </label>
                    <textarea
                      id="intro"
                      rows={5}
                      value={formData.content.intro}
                      onChange={(e) => handleContentChange('intro', e.target.value)}
                      className="w-full px-4 py-3 bg-[#0a0118] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-white placeholder-gray-600 leading-relaxed"
                      placeholder="Write an engaging introduction..."
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-300">
                        Body Sections
                      </label>
                      <button
                        type="button"
                        onClick={addSection}
                        className="inline-flex items-center px-4 py-2 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 text-sm font-medium rounded-lg transition-colors border border-blue-600/20"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add New Section
                      </button>
                    </div>

                    <div className="space-y-6">
                      {formData.content.sections.map((section, index) => (
                        <div key={index} className="border border-white/10 rounded-xl p-6 bg-[#0f0716] hover:border-white/20 transition-colors">
                          <div className="flex items-start justify-between mb-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-xs text-gray-400 font-mono border border-white/5">
                              #{index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeSection(index)}
                              className="text-gray-500 hover:text-red-400 transition-colors p-1"
                              title="Remove section"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>

                          <div className="space-y-4">
                            <input
                              type="text"
                              value={section.heading || ''}
                              onChange={(e) => handleSectionChange(index, 'heading', e.target.value)}
                              className="w-full px-4 py-3 bg-[#1a1025] border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-gray-500 font-medium"
                              placeholder="Section Heading"
                            />
                            <textarea
                              rows={6}
                              value={section.content || ''}
                              onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                              className="w-full px-4 py-3 bg-[#1a1025] border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-gray-500"
                              placeholder="Section content text..."
                            />

                            {/* Section Images */}
                            <div className="mt-4 pt-4 border-t border-white/5">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Images</span>
                                <button
                                  type="button"
                                  onClick={() => handleAddSectionImage(index)}
                                  className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                  Add Image
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(section.images || []).map((imageUrl, imgIndex) => (
                                  <div key={imgIndex} className="relative group">
                                    <ImageUpload
                                      label={`Image ${imgIndex + 1}`}
                                      currentImage={imageUrl}
                                      onImageSelect={(file) => handleSectionImageSelect(index, imgIndex, file)}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveSectionImage(index, imgIndex)}
                                      className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white rounded-full p-1.5 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                    {uploadProgress[`section-${index}-${imgIndex}`] !== undefined && (
                                      <div className="absolute inset-x-0 bottom-0 p-2 bg-black/80">
                                        <div className="w-full bg-gray-700 rounded-full h-1">
                                          <div
                                            className="bg-blue-500 h-1 rounded-full transition-all"
                                            style={{ width: `${uploadProgress[`section-${index}-${imgIndex}`]}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {(!section.images || section.images.length === 0) && (
                                  <div className="col-span-full py-4 text-center border border-dashed border-white/10 rounded-lg text-gray-600 text-sm">
                                    No images in this section
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {formData.content.sections.length === 0 && (
                      <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-xl bg-[#0f0716]">
                        <svg className="w-12 h-12 text-gray-700 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <p className="text-gray-500">Start adding content sections to your blog post</p>
                        <button
                          type="button"
                          onClick={addSection}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Add First Section
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Sidebar (4 cols) */}
            <div className="col-span-12 lg:col-span-4 space-y-8">

              {/* Publishing Options */}
              <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-lg">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Publishing</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-[#0a0118] rounded-lg border border-white/5">
                    <span className="text-gray-300 text-sm">Status</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${formData.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {formData.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <label className="flex items-center space-x-3 cursor-pointer p-1">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="is_published"
                        name="is_published"
                        checked={formData.is_published}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`block w-10 h-6 rounded-full transition-colors ${formData.is_published ? 'bg-blue-600' : 'bg-gray-700'}`}></div>
                      <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.is_published ? 'transform translate-x-4' : ''}`}></div>
                    </div>
                    <span className="text-sm text-gray-300">Publish immediately</span>
                  </label>
                </div>
              </div>

              {/* Categorization */}
              <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-lg">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Categorization</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      required
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-[#0a0118] border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-white text-sm"
                    >
                      <option value="Marketing">Marketing</option>
                      <option value="Security">Security</option>
                      <option value="Development">Development</option>
                      <option value="SEO">SEO</option>
                      <option value="IT">IT</option>
                      <option value="Design and branding">Design and branding</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="tags" className="block text-sm font-medium text-gray-300 mb-2">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2 p-2 bg-[#0a0118] border border-white/10 rounded-lg focus-within:border-blue-500 min-h-[42px] transition-all">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-md border border-blue-500/20 group"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="hover:text-blue-200 transition-colors"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        id="tags"
                        name="tags"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                        className="flex-1 bg-transparent border-none focus:outline-none text-white placeholder-gray-600 text-sm min-w-[120px]"
                        placeholder={formData.tags.length === 0 ? "marketing, seo, 2024" : ""}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Press comma or Enter to add a tag</p>
                  </div>
                </div>
              </div>

              {/* Author */}
              <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-lg">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Author</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-300 mb-2">
                      Author Name
                    </label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      required
                      value={formData.author}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-[#0a0118] border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-gray-600 text-sm"
                      placeholder="Jane Doe"
                    />
                  </div>
                </div>
              </div>

              {/* Featured Image */}
              <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-lg">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Featured Image</h3>
                <div className="space-y-4">
                  <ImageUpload
                    label="Blog Cover"
                    currentImage={formData.image}
                    onImageSelect={setBlogImageFile}
                  />
                  {uploadProgress.blog !== undefined && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-700 rounded-full h-1">
                        <div
                          className="bg-blue-500 h-1 rounded-full transition-all"
                          style={{ width: `${uploadProgress.blog}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Uploading... {uploadProgress.blog}%</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </AdminLayout >
  );
}

